import { useParams } from "react-router-dom";
import { genericEntityConstants } from "../../../api/GenericConstants";
import { IGenericRequestParameters, InferSortByOptions, StringIndexedDict } from "../../../api/GenericTypes";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { SingleEntityRoutingParams } from "../../../common/entity/EntityInterfaces";
import { useEntityDetail } from "../../../api/BaseEntityApi";
import { useContext, useEffect, useMemo } from "react";
import { LabNotebookEntryViewOnly } from "../../../ELN/LabNotebookEntry/LabNotebookEntryViewOnly/LabNotebookEntryViewOnly";
import { GenericEntityDetailTable } from "./GenericEntityDetailTable";
import { LabNotebookEntry } from "../../../ELN/types/LabNotebookEntry";
import { LoadingWrapper } from "../../../common/LoadingWrapper";
import { DetailsPageTopControlsView } from "../../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { SessionContext } from "../../../common/contexts/SessionContext";

export type ExportAction = "PDF" | "CSV";
export const exportActions = ["PDF", "CSV"] as ExportAction[];

interface GenericEntityExportViewProps<
  Entity extends GenericEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
> {}

export const GenericEntityExportView = <
  Entity extends GenericEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>
>({
  entityTypeId,
  componentProps,
}: GenericEntityComponentProps<Entity, GenericEntityExportViewProps<Entity, Filters>>) => {
  const { id } = useParams<SingleEntityRoutingParams>();

  const { entityConstants } = genericEntityConstants[entityTypeId] || {};

  const query = useMemo(() => {
    switch (entityTypeId) {
      case "notebookEntries":
        return { includeContent: true };
      default:
        return {};
    }
  }, [entityTypeId]);

  const idHandle = useMemo(() => {
    switch (entityTypeId) {
      case "methods":
        return id;
      default:
        return parseInt(id);
    }
  }, [entityTypeId, id]);

  const {
    data: entity,
    status,
    fetchStatus,
    error,
  } = useEntityDetail<Entity, Filters>(entityConstants.resource, idHandle, query as Filters, {
    enabled: !!entityConstants && !!idHandle,
  });

  const component = useMemo(() => {
    if (!entity) return null;
    switch (entityTypeId) {
      case "notebookEntries":
        return (
          <div style={{ width: "100%" }}>
            <LabNotebookEntryViewOnly
              entry={entity as unknown as LabNotebookEntry}
              useEntry
              ignoreIntersectionObserver
              printMode
            />
          </div>
        );
      default:
        return (
          <div className="flex gap-5" style={{ flexDirection: "column", width: "100%" }}>
            <DetailsPageTopControlsView
              entityTypeId={entityTypeId}
              entity={entity}
              entityConstants={entityConstants}
              hideHistoryModal
            />
            <div>
              <GenericEntityDetailTable
                entityTypeId={entityTypeId}
                entity={entity}
                componentProps={{ showHierarchy: true }}
              />
            </div>
          </div>
        );
    }
  }, [entity, entityConstants, entityTypeId]);

  const { setIsPrintContext } = useContext(SessionContext);
  useEffect(() => {
    setIsPrintContext?.(true);
    return () => setIsPrintContext?.((window as any).IS_PRINT_CONTEXT || window.matchMedia("print").matches);
  }, [setIsPrintContext]);

  return (
    <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error} type="skeleton-columns">
      <div
        className="flex"
        style={{
          height: "max-content",
          width: "100%",
          justifyContent: "center",
          padding: 20,
          minWidth: "max-content",
          backgroundColor: "var(--gray-100)",
        }}
      >
        <div
          style={{
            border: "1px solid var(--gray-300)",
            boxShadow: "1px 1px 1px 1px var(--gray-200)",
            padding: "15mm",
            backgroundColor: "white",
            overflow: "hidden",
            width: "210mm",
          }}
        >
          <div
            id="export-target"
            style={{
              backgroundColor: "white",
              padding: 0,
              margin: 0,
              width: "100%",
              overflow: "hidden",
              maxWidth: "100vw",
            }}
          >
            {component}
          </div>
        </div>
      </div>
    </LoadingWrapper>
  );
};
